// Sub-componentes visuales compartidos de los dashboards (Admin + ERP).
// Extraidos de DashboardAdmin.js para reutilizarlos sin duplicar.
import React, { useRef, useState, useEffect } from 'react';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { COL } from 'shared/darkColors';

// ── Formateadores ──
const money = (n) =>
  '$' +
  (Number(n) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const abbr = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'k';
  return '$' + n.toFixed(0);
};
const intFmt = (n) => (Number(n) || 0).toLocaleString('es-VE');
const pctFmt = (n) => `${(Number(n) || 0).toFixed(1)}%`;

function Delta({ actual, anterior }) {
  if (anterior === null || anterior === undefined) return null;
  if (anterior === 0) {
    if ((Number(actual) || 0) > 0) {
      return (
        <span style={{ ...chip, color: COL.pri2, background: 'var(--agx-blue-bg)' }}>nuevo</span>
      );
    }
    return null;
  }
  const pct = ((Number(actual) - anterior) / Math.abs(anterior)) * 100;
  const up = pct >= 0;
  return (
    <span
      style={{
        ...chip,
        color: up ? COL.green : COL.red,
        background: up ? 'var(--agx-green-bg)' : 'var(--agx-red-bg)',
      }}
    >
      {up ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

const chip = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  fontSize: '11px',
  fontWeight: 700,
  borderRadius: '6px',
  padding: '1px 6px',
};

function Sparkline({ data, color = COL.pri2, w = 88, h = 26 }) {
  const vals = (data || []).filter((v) => v !== null && v !== undefined).map(Number);
  if (vals.length < 2) return null;
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const pts = vals
    .map((v, i) => {
      const x = (i / (vals.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Card({ title, icon: Icon, color = COL.pri, hint, children, style }) {
  return (
    <div
      style={{
        background: COL.white,
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.03)',
        border: `1px solid ${COL.border}`,
        ...style,
      }}
    >
      {title && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          {Icon && <Icon size={15} color={color} />}
          <span style={{ fontSize: '14px', fontWeight: 700, color: COL.pri }}>{title}</span>
          {hint && (
            <span
              title={hint}
              style={{
                width: '15px',
                height: '15px',
                borderRadius: '50%',
                background: COL.bg,
                color: COL.muted,
                fontSize: '10px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'help',
                flexShrink: 0,
              }}
            >
              ?
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function Empty({ children = 'Sin datos' }) {
  return (
    <div style={{ color: COL.muted, fontSize: '12px', textAlign: 'center', padding: '24px 0' }}>
      {children}
    </div>
  );
}

function SkeletonBlock({ h = 80 }) {
  return <div className="dash-skel" style={{ height: h, borderRadius: '12px' }} />;
}

function LineChart({ series, labels, fmt = (v) => v, fmtTip, height = 210 }) {
  const tip = fmtTip || fmt;
  const wrapRef = useRef(null);
  const [w, setW] = useState(0);
  const [hi, setHi] = useState(null);
  const [hidden, setHidden] = useState({});
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const cw = el.clientWidth;
      if (cw) setW(cw);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect?.width;
      if (cw) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const n = labels.length;
  const W = Math.max(0, w),
    H = height,
    padL = 12,
    padR = 12,
    padT = 14,
    padB = 22;
  const innerW = W - padL - padR,
    innerH = H - padT - padB;
  const visible = series.filter((s) => !hidden[s.name]);
  const maxV = Math.max(1, ...visible.flatMap((s) => s.values));
  const X = (i) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const Y = (v) => padT + innerH - (Math.max(0, v) / maxV) * innerH;
  const gridY = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    v: maxV * t,
    y: padT + innerH - t * innerH,
  }));
  const linePath = (vals) =>
    vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
  const areaPath = (vals) =>
    `${linePath(vals)} L ${X(n - 1).toFixed(1)} ${Y(0).toFixed(1)} L ${X(0).toFixed(1)} ${Y(0).toFixed(1)} Z`;

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const idx = Math.round(((mx - padL) / innerW) * (n - 1));
    setHi(Math.max(0, Math.min(n - 1, idx)));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '14px', marginBottom: '6px', flexWrap: 'wrap' }}>
        {series.map((s) => (
          <button
            key={s.name}
            onClick={() => setHidden((h) => ({ ...h, [s.name]: !h[s.name] }))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              opacity: hidden[s.name] ? 0.4 : 1,
              padding: 0,
            }}
          >
            <span
              style={{ width: '11px', height: '11px', borderRadius: '3px', background: s.color }}
            />
            <span style={{ fontSize: '12px', fontWeight: 600, color: COL.text }}>{s.name}</span>
          </button>
        ))}
      </div>
      {n < 1 ? (
        <Empty />
      ) : (
        <svg
          width={W}
          height={H}
          style={{ display: 'block', overflow: 'visible' }}
          onMouseMove={onMove}
          onMouseLeave={() => setHi(null)}
        >
          <defs>
            <linearGradient id="dashArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COL.green} stopOpacity="0.18" />
              <stop offset="100%" stopColor={COL.green} stopOpacity="0" />
            </linearGradient>
          </defs>
          {gridY.map((g, i) => (
            <g key={i}>
              <line x1={padL} y1={g.y} x2={W - padR} y2={g.y} stroke={COL.border} strokeWidth="1" />
              <text x={padL} y={g.y - 3} fontSize="9" fill={COL.muted}>
                {fmt(g.v)}
              </text>
            </g>
          ))}
          {visible
            .filter((s) => s.area && n >= 2)
            .map((s) => (
              <path key={s.name} d={areaPath(s.values)} fill="url(#dashArea)" stroke="none" />
            ))}
          {n >= 2 &&
            visible.map((s) => (
              <path
                key={s.name}
                d={linePath(s.values)}
                fill="none"
                stroke={s.color}
                strokeWidth={s.dash ? 1.6 : 2.2}
                strokeDasharray={s.dash ? '5 4' : undefined}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
          {n === 1 &&
            visible.map((s) => (
              <circle
                key={s.name}
                cx={X(0)}
                cy={Y(s.values[0])}
                r="5"
                fill={COL.white}
                stroke={s.color}
                strokeWidth="2.2"
              />
            ))}
          {hi != null && (
            <g>
              <line
                x1={X(hi)}
                y1={padT}
                x2={X(hi)}
                y2={padT + innerH}
                stroke={COL.muted}
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              {visible.map((s) => (
                <circle
                  key={s.name}
                  cx={X(hi)}
                  cy={Y(s.values[hi])}
                  r="3.5"
                  fill={COL.white}
                  stroke={s.color}
                  strokeWidth="2"
                />
              ))}
            </g>
          )}
          {labels.map((lb, i) =>
            n <= 12 || i % Math.ceil(n / 8) === 0 ? (
              <text key={i} x={X(i)} y={H - 5} fontSize="9" fill={COL.muted} textAnchor="middle">
                {lb}
              </text>
            ) : null
          )}
        </svg>
      )}
      {hi != null && n >= 1 && (
        <div
          style={{
            position: 'absolute',
            top: '24px',
            left: `${(X(hi) / W) * 100}%`,
            transform: X(hi) > W / 2 ? 'translateX(-106%)' : 'translateX(6%)',
            background: COL.white,
            border: `1px solid ${COL.border}`,
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(16,24,40,0.12)',
            padding: '8px 10px',
            fontSize: '11px',
            pointerEvents: 'none',
            minWidth: '130px',
            zIndex: 5,
          }}
        >
          <div style={{ fontWeight: 700, color: COL.pri, marginBottom: '4px' }}>{labels[hi]}</div>
          {series.map((s) => (
            <div
              key={s.name}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                opacity: hidden[s.name] ? 0.4 : 1,
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span
                  style={{ width: '8px', height: '8px', borderRadius: '2px', background: s.color }}
                />
                {s.name}
              </span>
              <span style={{ fontWeight: 700 }}>{tip(s.values[hi])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Donut({ data, size = 128, thickness = 20 }) {
  const total = data.reduce((s, x) => s + (x.value || 0), 0);
  if (total <= 0) return <Empty />;
  const r = (size - thickness) / 2,
    cx = size / 2,
    cy = size / 2,
    circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={COL.bg} strokeWidth={thickness} />
        {data.map((x, i) => {
          const dash = ((x.value || 0) / total) * circ;
          const el = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={x.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div style={{ flex: 1, minWidth: '110px' }}>
        {data.map((x, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '3px 0',
              fontSize: '12px',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{ width: '10px', height: '10px', borderRadius: '3px', background: x.color }}
              />
              {x.label}
            </span>
            <span style={{ fontWeight: 700 }}>
              {x.display != null ? x.display : x.value}{' '}
              <span style={{ color: COL.muted, fontWeight: 600 }}>
                ({Math.round(((x.value || 0) / total) * 100)}%)
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarsH({ items, fmt = (v) => v, color = COL.pri2 }) {
  if (!items || items.length === 0) return <Empty />;
  const max = Math.max(...items.map((it) => it.value || 0), 1);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ marginBottom: '9px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              marginBottom: '3px',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {it.label}
            </span>
            <span style={{ fontWeight: 700, color: it.color || color, flexShrink: 0 }}>
              {fmt(it.value)}
            </span>
          </div>
          <div
            style={{ height: '7px', background: COL.bg, borderRadius: '4px', overflow: 'hidden' }}
          >
            <div
              style={{
                height: '100%',
                width: `${((it.value || 0) / max) * 100}%`,
                background: it.color || color,
                borderRadius: '4px',
              }}
            />
          </div>
          {it.sub && (
            <div style={{ fontSize: '10px', color: COL.muted, marginTop: '2px' }}>{it.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export { money, abbr, intFmt, pctFmt, Delta, Sparkline, Card, Empty, SkeletonBlock, LineChart, Donut, BarsH };
